import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Tab,
  Tabs,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { api } from '../services/api';

interface ParentUser {
  id: number;
  name?: string;
  first_name?: string;
  last_name?: string;
  email: string;
  phone_number?: string;
  phone?: string;
  status: string;
  created_at: string;
}

interface StudentUser {
  id: number;
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  phone?: string;
  status: string;
  created_at: string;
  board_name?: string;
  standard_name?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Users: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [parents, setParents] = useState<ParentUser[]>([]);
  const [students, setStudents] = useState<StudentUser[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    setPage(0);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (tabValue === 0) {
        // Load Parents
        const res = await api.users.getParents(params);
        setParents(res.data.parents || res.data.data || []);
        setTotalRecords(res.data.pagination?.total || res.data.total || 0);
      } else {
        // Load Students
        const res = await api.users.getStudents(params);
        setStudents(res.data.students || res.data.data || []);
        setTotalRecords(res.data.pagination?.total || res.data.total || 0);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load users');
      if (tabValue === 0) setParents([]);
      else setStudents([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getName = (user: any) => {
    if (user.name) return user.name;
    if (user.first_name) return `${user.first_name} ${user.last_name || ''}`.trim();
    return '-';
  };

  const getPhone = (user: any) => user.phone || user.phone_number || '-';

  const getStatusColor = (status: string) => {
    const s = status?.toLowerCase();
    switch (s) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const renderTable = () => {
    const isParent = tabValue === 0;
    const data = isParent ? parents : students;

    if (loading && data.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              {!isParent && <TableCell>Board</TableCell>}
              {!isParent && <TableCell>Standard</TableCell>}
              <TableCell>Status</TableCell>
              <TableCell>Joined</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isParent ? 6 : 8} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No {isParent ? 'parents' : 'students'} found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((user: any) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.id}</TableCell>
                  <TableCell>{getName(user)}</TableCell>
                  <TableCell>{user.email || '-'}</TableCell>
                  <TableCell>{getPhone(user)}</TableCell>
                  {!isParent && <TableCell>{user.board_name || '-'}</TableCell>}
                  {!isParent && <TableCell>{user.standard_name || '-'}</TableCell>}
                  <TableCell>
                     <Chip
                        label={user.status || 'Active'}
                        color={getStatusColor(user.status || 'Active') as any}
                        size="small"
                      />
                  </TableCell>
                  <TableCell>
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Users Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage parents and students registered on the platform
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="user tabs">
          <Tab label="Parents" />
          <Tab label="Students" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {renderTable()}
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        {renderTable()}
      </TabPanel>
    </Box>
  );
};

export default Users;
